import { Box, Button, Center, Flex, Heading, SimpleGrid, Grid, GridItem, AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, useColorModeValue } from "@chakra-ui/react"
import { faCancel } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { HotSpot, MediaMeta, NewContentItem } from "@xnav/types/src/common"
import { useState, useEffect, useRef, useContext } from "react"
import FuzzyFinder, { FuzzyFindWrapperProps } from "./fuzzyFinder"
import { useMedia, useReferenceMedia, useSelectedContent, useContentStructure, useContentStore, useSelectionReference } from "@/hooks/contextHooks"
import VideoMarkupPlayer from "./videoMarkupPlayer"
import MarkupPropertyEditor, { MarkupItem } from "./markupPropertyEditor"
import { EditContent, EditPolygon } from "@/pages/editor"
import MarkupTimeline from "./markupTimeline"
import MediaSingleSelectionCard from "./mediaSingleSelectionCard"
import { ReactStateType } from "@xnav/types/src/frontend"
import { MediaType } from "@/pages/mediaManager"
import { NavCollapsedContext } from "@/contexts/contexts"
import { updateContentItemInTree } from "@/functions/editorFunctions"

const renderWrapper = <T,>({ data, renderComponentProps, RenderComponent }: FuzzyFindWrapperProps<T>) => (
	<SimpleGrid padding={3} w='full' flex={1} minChildWidth='300px' spacing={3}>{data.map((d, i) => <RenderComponent key={i} data={d} {...renderComponentProps} />)}</SimpleGrid>
)

export type MarkupTool = 'pointer' | 'polygon' | 'hotspot' | 'triangle' | 'rectangle' | 'pentagon' | 'arrow' | 'line' | 'circle' | 'ellipse' | 'text'

export type SelectedMarkup = {
	type: 'polygon' | 'hotspot'
	index: number
	subIndex: number
}

export default function VideoMarkupEditor({
	tool,
	setTool,
}: {
	tool: MarkupTool
	setTool: React.Dispatch<React.SetStateAction<MarkupTool>>
}) {
	const [selectingNewVideo, setSelectingNewVideo] = useState<boolean>(false)
	const [media,] = useMedia()

	const [videoTime, setVideoTime] = useState<number>(0)
	const [videoPaused, setVideoPaused] = useState<boolean>(true)
	const [videoDuration, setVideoDuration] = useState<number>(0)
	const [isTrimming, setIsTrimming] = useState<boolean>(false)

	const [isExpanded, setIsExpanded] = useState<boolean>(false)
	const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState<boolean>(false)

	const [content, setContent] = useSelectedContent() as ReactStateType<EditContent>
	const [, setSectionMeta] = useContentStructure() as [any, (updater: (prev: any) => any) => void]
	const [, setContentStore] = useContentStore() as [any, React.Dispatch<React.SetStateAction<Record<string, EditContent>>>]
	const [selectionReference] = useSelectionReference() as [NewContentItem | undefined, any]

	const referenceMedia = useReferenceMedia()

	const [deleteTarget, setDeleteTarget] = useState<{ type: 'polygon' | 'hotspot', index: number } | null>(null);
	const cancelRef = useRef<HTMLButtonElement>(null);

	const handleDeleteConfirm = () => {
		if (deleteTarget) {
			const { type, index } = deleteTarget;
			if (type === 'polygon') {
				const newPolygons = content.polygons.filter((_, i) => i !== index) as EditPolygon[];
				setContent({
					...content,
					selectedMarkup: undefined,
					polygons: newPolygons
				});
			} else if (type === 'hotspot') {
				const newHotspots = content.hotSpots.filter((_, i) => i !== index);
				setContent({
					...content,
					selectedMarkup: undefined,
					hotSpots: newHotspots
				});
			}
		}
		setDeleteTarget(null);
	};

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			const target = e.target as HTMLElement;
			if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
				return;
			}

			if (e.code === 'Space') {
				e.preventDefault();
				setVideoPaused(prev => !prev);
			} else if (e.code === 'ArrowLeft') {
				e.preventDefault();
				const minT = (content?.clipStart !== undefined && content?.clipEnd !== undefined && content.clipEnd > content.clipStart) ? content.clipStart : 0;
				const jump = e.shiftKey ? 1.0 : 0.1;
				setVideoTime(prev => Math.max(minT, Math.round((prev - jump) * 10) / 10));
			} else if (e.code === 'ArrowRight') {
				e.preventDefault();
				const maxT = (content?.clipStart !== undefined && content?.clipEnd !== undefined && content.clipEnd > content.clipStart) ? content.clipEnd : videoDuration;
				const jump = e.shiftKey ? 1.0 : 0.1;
				setVideoTime(prev => Math.min(maxT, Math.round((prev + jump) * 10) / 10));
			} else if (e.code === 'Delete' || e.code === 'Backspace') {
				if (content?.selectedMarkup) {
					e.preventDefault();
					const { type, index } = content.selectedMarkup;
					setDeleteTarget({ type, index });
				}
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [content, setContent, videoDuration]);

	function setSelectedMarkup(markupItem: MarkupItem) {
		if (!content.selectedMarkup) return
		if (content.selectedMarkup.type === 'polygon') {
			const newPolygons = [...content.polygons]
			newPolygons[content.selectedMarkup.index] = markupItem as EditPolygon
			setContent({ ...content, polygons: newPolygons })
		} else if (content.selectedMarkup.type === 'hotspot') {
			const newHotspots = [...content.hotSpots]
			newHotspots[content.selectedMarkup.index] = markupItem as HotSpot
			setContent({ ...content, hotSpots: newHotspots })
		}
	}

	function handleDoneEditing() {
		setContent({ ...content, selectedMarkup: undefined })
	}

	const [fullScreen, setFullScreen] = useState<boolean>(false)

	if (media && selectingNewVideo) return (
		<Center padding={5} w='full' h='full'>
			<FuzzyFinder
				data={media.filter(m => m.type === MediaType.Video || String(m.type).toLowerCase() === 'video' || String(m.type) === '0')}
				searchKeys={['name', 'type']}
				RenderComponent={MediaSingleSelectionCard}
				renderComponentProps={{
					onSelect: (selectedMedia: MediaMeta) => {
						const targetId = content?.id || selectionReference?.id;
						const updatedContent: EditContent = {
							...content,
							id: targetId!,
							title: content?.title || selectionReference?.title || '',
							videoTitle: selectedMedia.name,
							videoId: selectedMedia.id,
							polygons: content?.polygons || [],
							hotSpots: content?.hotSpots || [],
							wisdomDocTitles: content?.wisdomDocTitles || [],
							clientScale: 1
						};
						setContent(updatedContent);
						if (targetId) {
							setContentStore(prev => ({
								...prev,
								[targetId]: updatedContent
							}));
							setSectionMeta(prev => prev ? updateContentItemInTree(prev, targetId, { videoId: selectedMedia.id, videoTitle: selectedMedia.name }) : prev);
						}
						referenceMedia(selectedMedia.id);
						setSelectingNewVideo(false);
					}
				}}
				RenderWrapper={renderWrapper}
			>
				<Button variant='inline' colorScheme='red' leftIcon={<FontAwesomeIcon icon={faCancel} />} onClick={() => setSelectingNewVideo(false)}>Cancel</Button>
			</FuzzyFinder>
		</Center>
	)

	const hasVideoAssigned = Boolean(
		content?.videoTitle ||
		content?.videoId ||
		(media && Array.isArray(media) && media.some((m: any) => m.references?.includes(content?.id) || m.name === content?.title))
	);

	if (!hasVideoAssigned) return (
		<Center flexDir='column' w='full' h='full' gap={5}>
			<Heading>No Video Selected</Heading>
			<Button size='lg' onClick={() => setSelectingNewVideo(true)}>Select Video</Button>
		</Center>
	)

	const [isNavCollapsed] = useContext(NavCollapsedContext)

	return (
		<Grid templateRows={fullScreen ? '1fr' : '1fr 220px'} w='full' h='100%' minH={0}>
			<GridItem display='flex' minH={0} w='full'>
				<Flex flexDir='column' flex={1} minW={0} minH={0}>
					<VideoMarkupPlayer
						content={content}
						videoPaused={videoPaused}
						videoTime={videoTime}
						videoDuration={videoDuration}
						fullScreen={fullScreen}
						tool={tool}
						setVideoPaused={setVideoPaused}
						setVideoTime={setVideoTime}
						setVideoDuration={setVideoDuration}
						setFullScreen={setFullScreen}
						setContent={setContent}
						setTool={setTool}
						isExpanded={isExpanded}
						isRightPanelCollapsed={isRightPanelCollapsed}
						isTrimming={isTrimming}
						setIsTrimming={setIsTrimming}
						onExpandRightPanel={() => setIsRightPanelCollapsed(false)}
						onSelectNewVideo={() => setSelectingNewVideo(true)}
					/>
				</Flex>
				{!fullScreen && (
					<Box w={isRightPanelCollapsed ? "42px" : (isExpanded ? "460px" : (isNavCollapsed ? "380px" : "330px"))} h='100%' overflow={'hidden'}>
						<MarkupPropertyEditor
							time={videoTime}
							markupItem={!content.selectedMarkup ? undefined : content.selectedMarkup.type === 'hotspot' ? content.hotSpots[content.selectedMarkup.index] : content.polygons[content.selectedMarkup.index]}
							setMarkupItem={setSelectedMarkup}
							onDone={handleDoneEditing}
							onDelete={() => { if (content?.selectedMarkup) setDeleteTarget(content.selectedMarkup); }}
							onDeleteMarkup={(type, index) => setDeleteTarget({ type, index })}
							isExpanded={isExpanded}
							onToggleExpand={() => setIsExpanded(prev => !prev)}
							isCollapsed={isRightPanelCollapsed}
							onToggleCollapse={() => setIsRightPanelCollapsed(prev => !prev)}
							onSelectMarkup={(type, index) => {
								setContent({
									...content,
									selectedMarkup: { type, index, subIndex: -1 }
								});
							}}
							onSeekTime={(t) => setVideoTime(t)}
							tool={tool}
							setTool={setTool}
							isTrimming={isTrimming}
							setIsTrimming={setIsTrimming}
							videoDuration={videoDuration}
						/>
					</Box>
				)}
			</GridItem>
			{!fullScreen && (
				<GridItem
					display='flex'
					w='full'
					maxW='full'
					h='220px'
					overflow='hidden'
					borderTop='1px solid'
					borderColor={useColorModeValue('gray.200', 'gray.750')}
					bg={useColorModeValue('white', 'gray.900')}
					minH={0}
				>
					<MarkupTimeline
						time={videoTime}
						videoDuration={videoDuration}
						setCurrentTime={setVideoTime}
					/>
				</GridItem>
			)}
			<AlertDialog
				isOpen={deleteTarget !== null}
				leastDestructiveRef={cancelRef}
				onClose={() => setDeleteTarget(null)}
			>
				<AlertDialogOverlay backdropFilter="blur(10px)" bg="rgba(0, 0, 0, 0.4)">
					<AlertDialogContent
						borderRadius="3xl"
						overflow="hidden"
						boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
						border="1px solid"
						borderColor={useColorModeValue("purple.100", "gray.750")}
						bg={useColorModeValue("white", "gray.850")}
						p={2}
					>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Delete Markup
						</AlertDialogHeader>

						<AlertDialogBody py={4} fontSize="md" color={useColorModeValue("gray.600", "gray.300")} fontWeight="medium">
							Are you sure you want to delete this markup?
						</AlertDialogBody>

						<AlertDialogFooter gap={3}>
							<Button ref={cancelRef} onClick={() => setDeleteTarget(null)} variant="ghost" borderRadius="full">
								Cancel
							</Button>
							<Button colorScheme="red" onClick={handleDeleteConfirm} borderRadius="full" px={6}>
								Delete
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Grid>
	)
}
